import React, { useRef } from 'react';
import { Box, Drawer, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { getInitials, colorForName } from './common/BusinessLogo';

const fmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/**
 * Product detail bottom sheet — opens when a product image is tapped.
 * Shows the enlarged image, name, unit/weight, price, and an Add / qty stepper,
 * shared by the customer dashboard shop (NewOrderView) and the public store.
 *
 * @param {object|null} product - { name, imageUrl, unitPrice, originalPrice, unitLabel, description }
 *                                 (null closes the sheet)
 * @param {number}   quantity   - current quantity of this product in the cart
 * @param {function} onAdd      - add the first unit
 * @param {function} onInc      - increment
 * @param {function} onDec      - decrement
 * @param {function} onClose
 */
export default function ProductDetailSheet({ product, quantity = 0, onAdd, onInc, onDec, onClose }) {
    // Keep the last product visible during the slide-down close animation
    const lastRef = useRef(product);
    if (product) lastRef.current = product;
    const p = product || lastRef.current;

    return (
        <Drawer
            anchor="bottom"
            open={!!product}
            onClose={onClose}
            sx={{ zIndex: 1500 }}
            PaperProps={{ sx: {
                borderTopLeftRadius: 20, borderTopRightRadius: 20,
                p: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                maxHeight: '90vh',
                // Match the order-page column instead of stretching full width.
                width: '100%', maxWidth: 720, mx: 'auto',
            } }}
        >
            {p && (
                <>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1,
                            bgcolor: 'background.paper', boxShadow: 1,
                            '&:hover': { bgcolor: 'action.hover' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    {/* Enlarged image */}
                    <Box sx={{
                        borderRadius: 3, overflow: 'hidden', bgcolor: 'grey.50', mb: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300,
                    }}>
                        {p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                            <Typography sx={{ fontSize: '4rem', fontWeight: 800, color: colorForName(p.name), textTransform: 'uppercase' }}>
                                {getInitials(p.name)}
                            </Typography>
                        )}
                    </Box>

                    {/* Name + unit + description */}
                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                        {p.name}
                    </Typography>
                    {p.unitLabel && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {p.unitLabel}
                        </Typography>
                    )}
                    {p.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
                            {p.description}
                        </Typography>
                    )}

                    {/* Price + Add / stepper */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography variant="h6" fontWeight={800}>{fmt(p.unitPrice)}</Typography>
                            {p.originalPrice > p.unitPrice && (
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                                    {fmt(p.originalPrice)}
                                </Typography>
                            )}
                        </Box>
                        {quantity === 0 ? (
                            <Button variant="outlined" onClick={onAdd}
                                sx={{ minWidth: 110, height: 44, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}>
                                Add
                            </Button>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', border: '1.5px solid', borderColor: 'primary.main', borderRadius: 2, overflow: 'hidden', height: 44 }}>
                                <IconButton onClick={onDec} sx={{ color: 'primary.main', borderRadius: 0 }}>
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography sx={{ minWidth: 36, textAlign: 'center', fontWeight: 700 }}>{quantity}</Typography>
                                <IconButton onClick={onInc} sx={{ color: 'primary.main', borderRadius: 0 }}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </Drawer>
    );
}
